import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  useUserProfile,
  useSetupProfileInvalidation,
} from "@/hooks/useUserProfile";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

export function ProtectedRoute({
  children,
  requireOnboarding = true,
}: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const {
    organization,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile();
  const location = useLocation();

  // Set up global queryClient reference for notifyProfileUpdate backward compatibility
  useSetupProfileInvalidation();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if user hasn't completed setup (no organization)
  // Skip this check if we're already on the onboarding page
  if (
    requireOnboarding &&
    !profileLoading &&
    !profileError &&
    !organization &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
